import { useEffect, useRef, useState } from 'react';
import { useOrganization } from '@/hooks/useQueries';
import { geocodeAddress } from '@/lib/mapUtils';
import type { MapCoordinates } from '@/lib/mapUtils';

/**
 * A coarse center for the org's service region, used to bias address geocoding
 * so a locally-typed street ranks above out-of-state matches (RUT-3034).
 *
 * Prefers the org's stored `coordinates` — geocoded server-side from the
 * company address, so no client geocode is needed. Falls back to a one-time
 * client geocode of the company address (e.g. right after onboarding, before
 * the backend has geocoded it). Returns `null` when no region is known, in
 * which case callers omit `proximity` and get the current national behavior.
 */
export function useOrgServiceRegionCenter(options?: { enabled?: boolean }): MapCoordinates | null {
  // Signup types an address before there is a company (or a login) to bias by.
  const { data } = useOrganization({ enabled: options?.enabled !== false });
  const org = data?.organization;

  const lng = org?.coordinates?.[0];
  const lat = org?.coordinates?.[1];
  const companyAddress = org?.companyAddress;

  const [center, setCenter] = useState<MapCoordinates | null>(null);
  // Guards the fallback geocode so it runs at most once per company address.
  const geocodedAddressRef = useRef<string | null>(null);

  useEffect(() => {
    // Preferred: coordinates the backend already geocoded from the company address.
    if (typeof lng === 'number' && typeof lat === 'number') {
      setCenter({ lng, lat });
      return;
    }

    // Fallback: geocode the company address once on the client.
    if (!companyAddress || geocodedAddressRef.current === companyAddress) return;
    geocodedAddressRef.current = companyAddress;

    let active = true;
    geocodeAddress(companyAddress)
      .then((result) => {
        if (active && result) setCenter({ lng: result.lng, lat: result.lat });
      })
      .catch(() => {
        // Leave center null → callers omit proximity (national search).
      });

    return () => {
      active = false;
    };
  }, [lng, lat, companyAddress]);

  return center;
}
